import type { RowData } from '@tanstack/react-table';

import { makeAccessorFunction } from '../../hooks/makeAccessorFunction.js';
import type { DataTableRowData } from '../../public.api.js';
import type {
  DataTableColumnThreshold,
  DataTableRowThreshold,
  DataTableThresholdsNormalizedRule,
} from './threshold-types.js';

type Accessor<TData extends DataTableRowData> =
  | string
  | number
  | symbol
  | ((row: TData) => unknown);

/**
 * @internal
 * Helper function that will normalize the given accessor to a function
 * in the normalized threshold rules.
 */
function makeAccessor<TData extends DataTableRowData>(
  accessor?: Accessor<TData>,
  fallbackAccessor?: Accessor<TData>,
): (row: TData) => unknown {
  // Check if the accessor is defined. If it is defined use this one for
  // the checks. If not, use the one from the columnDef.
  const appliedAccessor = accessor ?? fallbackAccessor;

  if (typeof appliedAccessor === 'string') {
    // Check if the string access contains JSONPath like characters, that would
    // require a complex replacer logic.
    if (appliedAccessor.includes('.') || appliedAccessor.includes('[')) {
      return makeAccessorFunction(appliedAccessor);
    } else {
      return (row: TData) => {
        // We can no longer infer this in the callback
        // oxlint-disable-next-line typescript/no-explicit-any
        return (row as any)[appliedAccessor];
      };
    }
  } else if (typeof appliedAccessor === 'function') {
    return appliedAccessor;
  }
  return () => undefined;
}

/**
 * Normalizes the simple rules and combined rules into a standard internal
 * rule set.
 * @internal
 */
export function normalizeThresholdRules<
  TData extends RowData,
  ThresholdRule extends
    | DataTableColumnThreshold<TData>[]
    | DataTableRowThreshold<TData>[],
>(
  thresholds: ThresholdRule,
  fallbackAccessor?: Accessor<TData>,
): Array<DataTableThresholdsNormalizedRule<TData>> {
  // Unify the rules into a single setup in order to combine simple and combined rules:
  const columnThresholdDefinitions: Array<
    DataTableThresholdsNormalizedRule<TData>
  > = [];

  for (const threshold of thresholds) {
    // Create the basis of the normalized rule.
    const unifiedRule: DataTableThresholdsNormalizedRule<TData> = {
      color: threshold.color,
      backgroundColor:
        'backgroundColor' in threshold ? threshold.backgroundColor : undefined,
      rules: [],
      type: 'type' in threshold ? threshold.type : undefined,
    };

    // Map the simple rules to an array as well, so we do not need to deal
    // with two different datatypes further down the line.
    const normalizedRulesArray = threshold.rules ?? [
      {
        accessor: 'accessor' in threshold ? threshold.accessor : undefined,
        value: 'value' in threshold ? threshold.value : undefined,
        comparator: threshold.comparator,
      },
    ];

    unifiedRule.rules = normalizedRulesArray.map((rule) => {
      return {
        // Accessors might be undefined in scenarios where a custom comparator function
        // is given.
        accessor:
          'accessor' in rule
            ? makeAccessor(rule.accessor, fallbackAccessor)
            : undefined,
        // Value might be undefined in scenarios where a custom comparator function
        // is given.
        value: 'value' in rule ? rule.value : undefined,
        comparator: rule.comparator,
      };
    });

    columnThresholdDefinitions.push(unifiedRule);
  }
  return columnThresholdDefinitions;
}
